import React, { useEffect, useState, useCallback } from 'react';
import { listFiles, getPresignedUrl, FileInfo } from '../api/nucleusApi';
import {
    Row,
    Column,
    Text,
    Button,
    Loader,
    Alert,
} from '@amzn/meridian';
import Icon from '@amzn/meridian/icon';
import folder from '@amzn/meridian-tokens/base/icon/folder';
import document from '@amzn/meridian-tokens/base/icon/document';
import caretRight from '@amzn/meridian-tokens/base/icon/caret-right';
import caretDown from '@amzn/meridian-tokens/base/icon/caret-down';
import DataViewer from './DataViewer';

interface FileBrowserProps {
    runId: string;
    onBack: () => void;
    sidebarOpen: boolean;
}

interface TreeNode {
    name: string;
    path: string;
    type: 'folder' | 'file';
    size?: number;
    lastModified?: string;
    children?: TreeNode[];
    loaded?: boolean;
    loading?: boolean;
}

const sortNodes = (nodes: TreeNode[]): TreeNode[] => {
    const folders = nodes.filter(n => n.type === 'folder').sort((a, b) => a.name.localeCompare(b.name));
    const files = nodes.filter(n => n.type === 'file').sort((a, b) => a.name.localeCompare(b.name));
    return [...folders, ...files];
};

// Calculate required width for a tree item (name + indent + icons + padding)
const calculateItemWidth = (name: string, depth: number): number => {
    // Approx: 8px per character + indent (12 + depth*20) + icons (32px) + padding (24px)
    const charWidth = 8;
    const indent = 12 + depth * 20;
    const iconsWidth = 32;
    const padding = 24;
    return name.length * charWidth + indent + iconsWidth + padding;
};

// Recursively find max width needed for all nodes
const calculateMaxWidth = (nodes: TreeNode[], depth: number = 0): number => {
    let maxWidth = 280; // minimum width
    for (const node of nodes) {
        const nodeWidth = calculateItemWidth(node.name, depth);
        if (nodeWidth > maxWidth) maxWidth = nodeWidth;
        if (node.children && node.children.length > 0) {
            const childMax = calculateMaxWidth(node.children, depth + 1);
            if (childMax > maxWidth) maxWidth = childMax;
        }
    }
    return Math.min(maxWidth, 600); // cap at 600px
};

const FileBrowser: React.FC<FileBrowserProps> = ({ runId, onBack, sidebarOpen }) => {
    const [tree, setTree] = useState<TreeNode[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPath, setSelectedPath] = useState<string | null>(null);
    const [openPaths, setOpenPaths] = useState<Set<string>>(new Set());
    const [hoveredPath, setHoveredPath] = useState<string | null>(null);
    const [viewingFile, setViewingFile] = useState<{ url: string; name: string } | null>(null);
    const [sidebarWidth, setSidebarWidth] = useState<number>(280);

    useEffect(() => {
        loadRootDirectory();
    }, [runId]);

    const loadRootDirectory = async () => {
        try {
            setLoading(true);
            setError(null);
            const prefix = `${runId}/`;
            const response = await listFiles(prefix);
            
            const nodes: TreeNode[] = [
                ...(response.folders || []).map((folderName) => ({
                    name: folderName,
                    path: `${prefix}${folderName}/`,
                    type: 'folder' as const,
                    children: [],
                    loaded: false,
                    loading: false,
                })),
                ...(response.files || []).map((file) => ({
                    name: file.name,
                    path: file.key,
                    type: 'file' as const,
                    size: file.size,
                    lastModified: file.lastModified,
                })),
            ];
            
            const sortedNodes = sortNodes(nodes);
            setTree(sortedNodes);
            // Calculate initial width based on root level items
            const newWidth = calculateMaxWidth(sortedNodes, 0);
            if (newWidth > sidebarWidth) {
                setSidebarWidth(newWidth);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load directory');
        } finally {
            setLoading(false);
        }
    };

    const loadFolderContents = async (path: string): Promise<TreeNode[]> => {
        const response = await listFiles(path);
        
        const nodes: TreeNode[] = [
            ...(response.folders || []).map((folderName) => ({
                name: folderName,
                path: `${path}${folderName}/`,
                type: 'folder' as const,
                children: [],
                loaded: false,
                loading: false,
            })),
            ...(response.files || []).map((file) => ({
                name: file.name,
                path: file.key,
                type: 'file' as const,
                size: file.size,
                lastModified: file.lastModified,
            })),
        ];
        
        return sortNodes(nodes);
    };

    const updateNodeInTree = useCallback((
        nodes: TreeNode[],
        targetPath: string,
        updater: (node: TreeNode) => TreeNode
    ): TreeNode[] => {
        return nodes.map((node) => {
            if (node.path === targetPath) {
                return updater(node);
            }
            if (node.children && node.children.length > 0) {
                return {
                    ...node,
                    children: updateNodeInTree(node.children, targetPath, updater),
                };
            }
            return node;
        });
    }, []);

    const getDescendantPaths = (nodes: TreeNode[], parentPath: string): string[] => {
        const paths: string[] = [];
        const findDescendants = (nodeList: TreeNode[]) => {
            for (const n of nodeList) {
                if (n.path.startsWith(parentPath) && n.path !== parentPath) {
                    paths.push(n.path);
                    if (n.children) findDescendants(n.children);
                }
            }
        };
        findDescendants(nodes);
        return paths;
    };

    const handleFolderClick = async (node: TreeNode) => {
        const isOpen = openPaths.has(node.path);
        
        if (isOpen) {
            const newOpenPaths = new Set(openPaths);
            newOpenPaths.delete(node.path);
            const descendantPaths = getDescendantPaths(tree, node.path);
            descendantPaths.forEach(p => newOpenPaths.delete(p));
            setOpenPaths(newOpenPaths);
        } else {
            if (!node.loaded) {
                setTree((prev) => updateNodeInTree(prev, node.path, (n) => ({ ...n, loading: true })));
                try {
                    const children = await loadFolderContents(node.path);
                    // Calculate depth of this folder
                    const depth = node.path.split('/').filter(Boolean).length;
                    // Update max sidebar width based on new children
                    const childrenWidth = calculateMaxWidth(children, depth);
                    if (childrenWidth > sidebarWidth) {
                        setSidebarWidth(childrenWidth);
                    }
                    setTree((prev) => updateNodeInTree(prev, node.path, (n) => ({
                        ...n, children, loaded: true, loading: false,
                    })));
                } catch (err) {
                    setTree((prev) => updateNodeInTree(prev, node.path, (n) => ({ ...n, loading: false })));
                    alert(`Failed to load folder: ${err instanceof Error ? err.message : 'Unknown error'}`);
                    return;
                }
            }
            const newOpenPaths = new Set(openPaths);
            newOpenPaths.add(node.path);
            setOpenPaths(newOpenPaths);
        }
        setSelectedPath(node.path);
    };

    const handleFileClick = async (node: TreeNode) => {
        setSelectedPath(node.path);
        
        // Check if it's a CSV file - open in DataViewer
        if (node.name.toLowerCase().endsWith('.csv')) {
            try {
                const { url } = await getPresignedUrl(node.path);
                setViewingFile({ url, name: node.name });
            } catch (err) {
                alert(`Failed to get file URL: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
        } else {
            // For non-CSV files, open in new tab (original behavior)
            try {
                const { url } = await getPresignedUrl(node.path);
                window.open(url, '_blank');
            } catch (err) {
                alert(`Failed to download: ${err instanceof Error ? err.message : 'Unknown error'}`);
            }
        }
    };

    const handleCloseDataViewer = () => {
        setViewingFile(null);
    };

    const treeItemStyle = (indent: number, selected: boolean, hovered: boolean): React.CSSProperties => ({
        paddingLeft: indent, paddingTop: 6, paddingBottom: 6, paddingRight: 12,
        cursor: 'pointer',
        backgroundColor: selected ? 'rgba(0, 115, 187, 0.1)' : hovered ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
        borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4,
        minHeight: '24px',
    });

    const iconWrapper: React.CSSProperties = { 
        width: '16px', 
        height: '16px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexShrink: 0,
    };
    
    const iconSmall: React.CSSProperties = { transform: 'scale(0.8)' };

    const renderTreeNode = (node: TreeNode, depth: number = 0): React.ReactNode => {
        const indent = 12 + depth * 20;
        if (node.type === 'folder') {
            const isOpen = openPaths.has(node.path);
            const hasChildren = node.children && node.children.length > 0;
            let children: React.ReactNode = null;
            if (isOpen) {
                if (node.loading) {
                    children = <div key={`${node.path}-loading`} style={{ paddingLeft: indent + 20, paddingTop: 4 }}><Loader size="small" /></div>;
                } else if (hasChildren) {
                    children = node.children!.map((child) => renderTreeNode(child, depth + 1));
                } else if (node.loaded) {
                    children = <div key={`${node.path}-empty`} style={{ paddingLeft: indent + 20 }}><Text type="b100" color="secondary">(empty)</Text></div>;
                }
            }
            return (
                <React.Fragment key={node.path}>
                    <div 
                        style={treeItemStyle(indent, selectedPath === node.path, hoveredPath === node.path)} 
                        onClick={() => handleFolderClick(node)}
                        onMouseEnter={() => setHoveredPath(node.path)}
                        onMouseLeave={() => setHoveredPath(null)}
                    >
                        <span style={iconWrapper}><span style={iconSmall}><Icon tokens={isOpen ? caretDown : caretRight} /></span></span>
                        <span style={iconWrapper}><span style={iconSmall}><Icon tokens={folder} /></span></span>
                        <span style={{ position: 'relative', left: '6px', top: '6px' }}><Text type="b200">{node.name}</Text></span>
                        {node.loading && <Loader size="small" />}
                    </div>
                    {children}
                </React.Fragment>
            );
        }
        return (
            <div 
                key={node.path} 
                style={treeItemStyle(indent + 20, selectedPath === node.path, hoveredPath === node.path)} 
                onClick={() => handleFileClick(node)}
                onMouseEnter={() => setHoveredPath(node.path)}
                onMouseLeave={() => setHoveredPath(null)}
            >
                <span style={iconWrapper}><span style={iconSmall}><Icon tokens={document} /></span></span>
                <span style={{ position: 'relative', left: '6px', top: '6px' }}><Text type="b200">{node.name}</Text></span>
            </div>
        );
    };

    if (loading) {
        return <Column alignmentHorizontal="center" spacing="400" spacingInset="500"><Loader size="large" /><Text>Loading files...</Text></Column>;
    }

    if (error) {
        return <Column spacing="400" spacingInset="400"><Alert type="error" title="Error loading files">{error}</Alert><Row><Button onClick={loadRootDirectory}>Retry</Button></Row></Column>;
    }

    return (
        <>
            {/* Sidebar - File Tree */}
            <div style={{ 
                width: sidebarOpen ? `${sidebarWidth}px` : '0px', 
                minWidth: sidebarOpen ? `${sidebarWidth}px` : '0px',
                maxWidth: sidebarOpen ? `${sidebarWidth}px` : '0px',
                borderRight: sidebarOpen ? '1px solid #e0e0e0' : 'none',
                backgroundColor: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease',
                height: '100%'
            }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <Button type="tertiary" size="small" onClick={onBack}>← Back to Runs</Button>
                    <div style={{ marginTop: '8px' }}>
                        <Text type="b200" color="secondary">Run: {runId}</Text>
                    </div>
                </div>
                <div style={{ 
                    flex: 1, 
                    overflow: 'auto', 
                    padding: '8px 0'
                }}>
                    {tree.length > 0 
                        ? tree.map((node) => renderTreeNode(node, 0)) 
                        : <div style={{ padding: '12px 16px' }}><Text type="b200" color="secondary">This folder is empty</Text></div>
                    }
                </div>
            </div>
            
            {/* Main Content Area */}
            <div style={{ 
                flex: 1, 
                padding: '20px',
                overflow: 'auto',
                backgroundColor: 'white',
                height: '100%'
            }}>
                {viewingFile ? (
                    <DataViewer 
                        fileUrl={viewingFile.url} 
                        fileName={viewingFile.name} 
                        onClose={handleCloseDataViewer}
                    />
                ) : selectedPath ? (
                    <Column spacing="300">
                        <Text type="h200">File Selected</Text>
                        <Text color="secondary">Selected: {selectedPath}</Text>
                        <Alert title="File Info">
                            Click a CSV file to view its contents in the data viewer.
                            Non-CSV files will open in a new tab.
                        </Alert>
                    </Column>
                ) : (
                    <Column alignmentHorizontal="center" alignmentVertical="center" spacing="300" height="100%">
                        <Text type="h200" color="secondary">Select a file</Text>
                        <Text color="secondary">Click on a CSV file in the sidebar to view its contents</Text>
                    </Column>
                )}
            </div>
        </>
    );
};

export default FileBrowser;
